// Shared cards: group, event, course, practice.
// Used by Home, Groups/Events browse pages, Courses/Practices browse pages,
// and the Course/Practice detail pages (Related strips).
import { useNavigate } from 'react-router-dom';
import { Avatar } from './ui';
import { formatDateUS, stripHtml } from '../lib/format';
import { cssBackground } from './ColorField';
import api from '../lib/api';
import '../styles/learn.css';

// --- Learn cards -----------------------------------------------------------

export function priceLabel(c) {
  if (!c?.paid) return 'Free';
  return (c.priceCents / 100).toLocaleString(undefined, { style: 'currency', currency: 'usd' });
}

// design/024 — "created less than 7 days ago" test used by the NEW badge on
// catalog cards. Robust against missing/invalid createdAt (returns false).
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
export function isNew(createdAt) {
  if (!createdAt) return false;
  const t = new Date(createdAt).getTime();
  if (Number.isNaN(t)) return false;
  return (Date.now() - t) < WEEK_MS;
}

/**
 * NEW badge — bright pink/red pill shown on catalog cards for items <7 days
 * old. Deliberately loud so it pops next to the muted JOINED (green) and
 * PricePill (blue/amber) badges.
 */
function NewPill({ overlay = false }) {
  const base = {
    fontSize: overlay ? '0.75rem' : '0.6875rem',
    fontWeight: 800,
    letterSpacing: 0.6,
    color: '#ffffff',
    backgroundColor: '#e11d48',
    border: '1px solid #be123c',
    borderRadius: 999,
    padding: overlay ? '3px 10px' : '2px 8px',
    boxShadow: overlay ? '0 2px 6px rgba(225,29,72,0.4)' : undefined,
    textTransform: 'uppercase',
    display: 'inline-block',
  };
  return <span style={base}>New</span>;
}

/** Two-line clamp for card descriptions. */
const clamp2 = { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' };

function EnrolledPill() {
  return (
    <span style={{
      fontSize: '0.6875rem', fontWeight: 700, letterSpacing: 0.4,
      color: '#065f46', backgroundColor: '#d1fae5', border: '1px solid #6ee7b7',
      borderRadius: 999, padding: '2px 8px',
    }}>ENROLLED</span>
  );
}

/**
 * Course card — Courses browse, Home "My Courses", search. Same layout as
 * Group / Event cards (2026-10-09): icon tile (cover image, else cover color
 * + letter), title with NEW / ENROLLED / price pills, instructor line.
 */
export function CourseCardView({ c, onOpen, hideStatusBadge = false }) {
  const navigate = useNavigate();
  const open = onOpen || (() => navigate(`/learn/course/${c.id}`));
  const lessons = `${c.lessonCount} lesson${c.lessonCount === 1 ? '' : 's'}`;
  const summary = stripHtml(c.summary || '');
  return (
    <button className="item-card" onClick={open} style={c.locked ? { opacity: 0.85 } : undefined}>
      <div className="item-head">
        <Avatar name={c.title} src={c.coverImageUrl ? api.iconUrl(c.coverImageUrl) : null} square
          size={64} radius={16} bg={cssBackground(c.iconColor || c.coverColor)} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="item-title" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span>{c.title}</span>
            {isNew(c.createdAt) && <NewPill />}
            {c.enrolled && <EnrolledPill />}
            {!hideStatusBadge && !c.enrolled && <PricePill cents={c.paid ? c.priceCents : 0} />}
          </div>
          <div className="item-leader">
            {[lessons, c.instructorUsername ? `Instructor @${c.instructorUsername}` : null].filter(Boolean).join(' · ')}
          </div>
        </div>
      </div>
      {summary && <div className="item-body" style={clamp2}>{summary}</div>}
    </button>
  );
}

/**
 * Practice card — Practices browse, Home "My Practices", search. Same layout
 * as Group / Event cards. Practices are ongoing — no lesson count.
 */
export function PracticeCardView({ p, onOpen, hideStatusBadge = false }) {
  const navigate = useNavigate();
  const open = onOpen || (() => navigate(`/learn/practice/${p.id}`));
  const type = p.primaryType && p.primaryType !== 'TEXT' ? p.primaryType.toLowerCase() : null;
  return (
    <button className="item-card" onClick={open} style={p.locked ? { opacity: 0.85 } : undefined}>
      <div className="item-head">
        <Avatar name={p.title} src={p.coverImageUrl ? api.iconUrl(p.coverImageUrl) : null} square
          size={64} radius={16} bg={cssBackground(p.iconColor || p.headerColor)} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="item-title" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span>{p.title}</span>
            {isNew(p.createdAt) && <NewPill />}
            {p.enrolled && <EnrolledPill />}
            {!hideStatusBadge && !p.enrolled && <PricePill cents={p.paid ? p.priceCents : 0} />}
          </div>
          {(type || p.instructorUsername) && (
            <div className="item-leader">
              {[type, p.instructorUsername ? `Instructor @${p.instructorUsername}` : null].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
      </div>
      {p.subtitle && <div className="item-body" style={clamp2}>{p.subtitle}</div>}
    </button>
  );
}

export function GroupCard({ group, linkPrefix = '/group', fromHome = false }) {
  const navigate = useNavigate();
  const members = group.members?.length;
  return (
    <button className="item-card" onClick={() => navigate(`${linkPrefix}/${group.id}`, fromHome ? { state: { from: 'home' } } : undefined)}>
      <div className="item-head">
        <Avatar name={group.name} src={api.iconUrl(group.eventIconUrl)} square
          bg={(group.iconColor || group.coverColor) ? cssBackground(group.iconColor || group.coverColor) : undefined} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="item-title" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span>{group.name}</span>
            {group.draft ? <DraftPill /> : (isNew(group.createdAt) && <NewPill />)}
            {group.joined && <JoinedPill />}
            <PricePill cents={group.priceCents} />
          </div>
          <div className="item-leader">
            {group.admin ? `led by @${group.admin.username}` : 'no leader'}
            {members != null ? ` · ${members} member${members === 1 ? '' : 's'}` : ''}
          </div>
        </div>
      </div>
      {group.description && <div className="item-body">{group.description}</div>}
    </button>
  );
}

/**
 * Free / price badge shown on GroupCard + EventCard so the browse pages
 * make the fee explicit (design/024 followup). `cents == 0 or null` → FREE
 * pill; anything > 0 → "$X.XX" pill.
 */
function PricePill({ cents }) {
  const paid = (cents || 0) > 0;
  const style = paid
    ? { color: '#78350f', backgroundColor: '#fef3c7', border: '1px solid #fcd34d' }
    : { color: '#1e3a8a', backgroundColor: '#dbeafe', border: '1px solid #93c5fd' };
  return (
    <span style={{
      fontSize: '0.6875rem', fontWeight: 700, letterSpacing: 0.4,
      borderRadius: 999, padding: '2px 8px',
      ...style,
    }}>{paid ? `$${(cents / 100).toFixed(2)}` : 'FREE'}</span>
  );
}

/** Unsaved "+ New" draft — only its creator sees it (V46). */
function DraftPill() {
  return (
    <span style={{
      fontSize: '0.6875rem', fontWeight: 700, letterSpacing: 0.4,
      color: '#6b7280', backgroundColor: '#f3f4f6', border: '1px dashed #9ca3af',
      borderRadius: 999, padding: '2px 8px',
    }}>DRAFT — not created yet</span>
  );
}

function JoinedPill() {
  return (
    <span style={{
      fontSize: '0.6875rem', fontWeight: 700, letterSpacing: 0.4,
      color: '#065f46', backgroundColor: '#d1fae5', border: '1px solid #6ee7b7',
      borderRadius: 999, padding: '2px 8px',
    }}>JOINED</span>
  );
}

export function EventCard({ event, linkPrefix = '/event', fromHome = false }) {
  const navigate = useNavigate();
  const title = event.requestName || '(unnamed request)';
  const state = (event.state || '').toLowerCase();
  return (
    <button className="item-card" onClick={() => navigate(`${linkPrefix}/${event.id}`, fromHome ? { state: { from: 'home' } } : undefined)}>
      <div className="item-head">
        <Avatar name={title} src={api.iconUrl(event.eventIconUrl)} square
          bg={(event.iconColor || event.coverColor) ? cssBackground(event.iconColor || event.coverColor) : undefined} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="item-title" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span>{title}</span>
            {event.draft ? <DraftPill /> : (isNew(event.createdAt) && <NewPill />)}
            {event.joined && <JoinedPill />}
            <PricePill cents={event.priceCents} />
          </div>
          <div className="item-leader">{event.ownerUsername ? `led by @${event.ownerUsername}` : 'no leader'}</div>
          {event.topic && <div className="item-body">{event.topic}</div>}
          {event.groupName && <div className="item-chip">{event.groupName}</div>}
        </div>
      </div>
      <div className="item-meta">
        <span>{formatDateUS(event.date)}{event.beginTime ? ` · ${event.beginTime}` : ''}</span>
        {state && <span>{state}</span>}
      </div>
    </button>
  );
}
